import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useMemo } from 'react';

import useGridTheme from '@/hooks/useGridTheme';

import type { ResponseActionSummaryDTO } from './MsDefenderIntegration.types';
import { RESPONSE_ACTION_HISTORY_COLUMN_DEFS } from './ResponseActionHistoryDialog.constants';
import type { ResponseActionHistoryDialogProps } from './ResponseActionHistoryDialog.types';

export const ResponseActionHistoryDialog = ({
  open,
  onClose,
  actions,
  tenantId
}: ResponseActionHistoryDialogProps) => {
  const gridTheme = useGridTheme();
  const historyGridTheme = useMemo(
    () =>
      gridTheme.withParams({
        backgroundColor: 'var(--mui-palette-background-paper)'
      }),
    [gridTheme]
  );
  const rowData = useMemo(() => actions, [actions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '95vw', sm: '90vw', lg: '85vw', xl: '75vw' },
            maxWidth: '1800px',
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '0.0625rem solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          Response Action History ({actions.length})
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close dialog">
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2, flex: 1, overflowY: 'auto' }}>
        <Typography variant="body1" color="text.secondary" sx={{ my: 2 }}>
          Tenant: <b>{tenantId}</b> · Showing response actions recorded in the
          last 30 days.
        </Typography>

        {actions.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 4, textAlign: 'center' }}
          >
            No response actions recorded for this tenant in the last 30 days.
          </Typography>
        ) : (
          <AgGridReact<ResponseActionSummaryDTO>
            rowData={rowData}
            columnDefs={RESPONSE_ACTION_HISTORY_COLUMN_DEFS}
            domLayout="autoHeight"
            theme={historyGridTheme}
            rowHeight={40}
            getRowId={(params) => String(params.data.id)}
            suppressCellFocus
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '0.0625rem solid',
          borderColor: 'divider'
        }}
      >
        <Button variant="contained" size="small" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
